// Round to nearest increment (default 2.5kg)
export function roundWeight(weight, increment = 2.5) {
  return Math.floor(weight / increment) * increment;
}

// Calculate working weight from percentage and 1RM
export function calcWeight(percentage, oneRM, increment = 2.5) {
  return roundWeight(oneRM * percentage, increment);
}

// Generate warm-up ramp based on working weight
export function generateWarmup(workingWeight, increment = 2.5) {
  if (!workingWeight || workingWeight <= 20) return [];
  const ramp = [
    { percentage: 0.40, reps: 5 },
    { percentage: 0.55, reps: 3 },
    { percentage: 0.70, reps: 2 },
    { percentage: 0.80, reps: 1 },
  ];
  const sets = [];
  const bar = 20;
  for (const step of ramp) {
    const weight = roundWeight(workingWeight * step.percentage, increment);
    if (weight >= bar && weight < workingWeight) {
      if (sets.length === 0 || sets[sets.length - 1].weight !== weight) {
        sets.push({ weight, reps: step.reps, isWarmup: true });
      }
    }
  }
  if (sets.length > 0 && sets[0].weight > bar && workingWeight > 40) {
    sets.unshift({ weight: bar, reps: 5, isWarmup: true });
  }
  return sets;
}

// Epley formula: estimated 1RM = weight × (1 + reps/30)
export function epley1RM(weight, reps) {
  if (reps <= 0 || weight <= 0) return 0;
  if (reps === 1) return weight;
  return Math.round(weight * (1 + reps / 30) * 10) / 10;
}

// DOTS coefficient for males
export function calcDOTS(total, bodyweight) {
  if (!total || !bodyweight || bodyweight <= 0) return 0;
  const a = -307.75076;
  const b = 24.0900756;
  const c = -0.1918759221;
  const d = 0.0007391293;
  const e = -0.000001093;
  const bw = bodyweight;
  const denominator = a + b * bw + c * bw * bw + d * bw * bw * bw + e * bw * bw * bw * bw;
  if (denominator <= 0) return 0;
  return Math.round((500 / denominator) * total * 100) / 100;
}

// Calculate session tonnage
export function calcTonnage(exercises) {
  let total = 0;
  for (const ex of exercises) {
    const weight = parseFloat(ex.loggedWeight) || 0;
    const reps = parseFloat(ex.loggedReps) || 0;
    const sets = parseFloat(ex.loggedSets || ex.sets) || 0;
    total += weight * reps * sets;
  }
  return Math.round(total);
}

// Get the first working weight from a session's exercises
export function getFirstWorkingWeight(exercises, oneRM, increment = 2.5) {
  for (const ex of exercises) {
    if (ex.percentage) {
      return calcWeight(ex.percentage, oneRM, increment);
    }
    // Imported linear-progression programs prescribe an absolute weight
    if (typeof ex.weight === 'number' && ex.weight > 0) {
      return ex.weight;
    }
  }
  return null;
}

// Format weight for display
export function formatWeight(weight) {
  if (!weight && weight !== 0) return '—';
  return weight % 1 === 0 ? `${weight}` : `${weight.toFixed(1)}`;
}

// =====================================================
// AMAP-based training max adjustment (all values in kg)
// =====================================================
// Standard PDF rules (2-tier: same / +2.5kg / +5kg)
// Used by most beginner and intermediate programs
//
// Three-tier rules (+2.5kg / +5kg / +7.5kg)
// Used by 3x/week beginner programs (bench_3x_beg, squat_3x_beg)

export function getMaxAdjustment(percentage, reps, threeTier = false) {
  if (threeTier) {
    return getThreeTierAdjustment(percentage, reps);
  }

  // ~90%
  if (percentage >= 0.88) {
    if (reps <= 3) return 0;
    if (reps <= 5) return 2.5;
    return 5;
  }
  // ~85%
  if (percentage >= 0.83) {
    if (reps <= 5) return 0;
    if (reps <= 7) return 2.5;
    return 5;
  }
  // ~80%
  if (percentage >= 0.78) {
    if (reps <= 8) return 0;
    if (reps <= 11) return 2.5;
    return 5;
  }
  // ~75%
  if (percentage >= 0.73) {
    if (reps <= 10) return 0;
    if (reps <= 12) return 2.5;
    return 5;
  }
  // ~70%
  if (reps <= 8) return 0;
  if (reps <= 10) return 2.5;
  return 5;
}

// Three-tier adjustment for 3x/week beginner programs
function getThreeTierAdjustment(percentage, reps) {
  // ~80%
  if (percentage >= 0.78) {
    if (reps <= 4) return 0;
    if (reps <= 6) return 2.5;
    if (reps <= 8) return 5;
    return 7.5;
  }
  // ~75%
  if (percentage >= 0.73) {
    if (reps <= 6) return 0;
    if (reps <= 8) return 2.5;
    if (reps <= 10) return 5;
    return 7.5;
  }
  // ~70%
  if (reps <= 8) return 0;
  if (reps <= 10) return 2.5;
  if (reps <= 12) return 5;
  return 7.5;
}

// Calculate new training max from a rep max result
// 10RM = 75%, 8RM = 80%, 5RM = 85%, 3RM = 90%
export function rmToMax(weight, rmType, increment = 2.5) {
  const percentages = { '10RM': 0.75, '8RM': 0.80, '5RM': 0.85, '3RM': 0.90 };
  const pct = percentages[rmType];
  if (!pct || !weight || weight <= 0) return null;
  return roundWeight(weight / pct, increment);
}

// Detect if an RM string is a rep max type we can calculate from
export function parseRMType(weightStr) {
  if (!weightStr || typeof weightStr !== 'string') return null;
  const match = weightStr.match(/^(\d+)RM$/i);
  if (match) {
    const rm = `${match[1]}RM`;
    if (['10RM', '8RM', '5RM', '3RM'].includes(rm)) return rm;
  }
  return null;
}

// Get closest bodyweight to a given date
export function getClosestBodyweight(bodyweights, targetDate) {
  if (!bodyweights || bodyweights.length === 0) return null;
  const target = new Date(targetDate).getTime();
  let closest = bodyweights[0];
  let minDiff = Math.abs(new Date(closest.date).getTime() - target);
  for (const bw of bodyweights) {
    const diff = Math.abs(new Date(bw.date).getTime() - target);
    if (diff < minDiff) {
      minDiff = diff;
      closest = bw;
    }
  }
  return closest;
}

// Main lift names per lift type - used to determine if an AMAP set
// should trigger training max adjustment (only main compound lifts, not variants)
export const MAIN_LIFT_NAMES = {
  bench: ['Bench Press'],
  squat: ['Squat', 'Low Bar Squat'],
  deadlift: ['Deadlift (primary stance)', 'Deadlift'],
};

// Programs that use three-tier adjustment rules
export const THREE_TIER_PROGRAMS = ['bench_3x_beg', 'squat_3x_beg'];

// Programs that track per-day working weights independently
// Each day's AMAP adjusts only that day's weight for the next week
export const PER_DAY_WEIGHT_PROGRAMS = ['bench_3x_beg', 'squat_3x_beg'];

// Initial percentages for per-day programs (by dayIndex)
export const PER_DAY_PERCENTAGES = {
  0: 0.70, // Day 1
  1: 0.75, // Day 2
  2: 0.80, // Day 3
};
